import { type ClientContext, ContextClient } from '~/services/contextClient';
import AiMessageVettingAgentsClientFactory from './aiMessageVettingAgentsClient.factory';
import type {
    AiMessageVettingAgent,
    AiMessageVettingAgentPayload,
    AiMessageVettingAgentsClientInterface,
    GetAiMessageVettingAgentsParams,
    GetAiMessageVettingAgentMessageStandardCountsResponse,
    GetAiSuggestionsParams,
    TestAiVettingAgentParams,
    TestAiVettingAgentResponse,
} from './aiMessageVettingAgentsClient.types';
import type { CursorPaginationResponse } from '~/services/api';
import type { AxiosPromise } from 'axios';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';

class AiMessageVettingAgentsClient extends ContextClient<AiMessageVettingAgentsClientInterface> {
    constructor() {
        super(AiMessageVettingAgentsClientFactory);
    }

    getAiMessageVettingAgents(
        context: ClientContext,
        params?: GetAiMessageVettingAgentsParams
    ): AxiosPromise<CursorPaginationResponse<AiMessageVettingAgent>> {
        const client = this.getClient(context);
        return client.getAiMessageVettingAgents(params);
    }

    getAiMessageVettingAgent(context: ClientContext, agentId: number): AxiosPromise<AiMessageVettingAgent> {
        const client = this.getClient(context);
        return client.getAiMessageVettingAgent(agentId);
    }

    createAiMessageVettingAgent(
        context: ClientContext,
        data: AiMessageVettingAgentPayload
    ): AxiosPromise<AiMessageVettingAgent> {
        const client = this.getClient(context);
        return client.createAiMessageVettingAgent(data);
    }

    updateAiMessageVettingAgent(
        context: ClientContext,
        agentId: number,
        data: AiMessageVettingAgentPayload
    ): AxiosPromise<AiMessageVettingAgent> {
        const client = this.getClient(context);
        return client.updateAiMessageVettingAgent(agentId, data);
    }

    deleteAiMessageVettingAgent(context: ClientContext, agentId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.deleteAiMessageVettingAgent(agentId);
    }

    getAiMessageVettingAgentMessageStandardCounts(
        context: ClientContext,
        params: {
            ai_message_vetting_agent_ids: number[];
        }
    ): AxiosPromise<GetAiMessageVettingAgentMessageStandardCountsResponse> {
        const client = this.getClient(context);
        return client.getAiMessageVettingAgentMessageStandardCounts(params);
    }

    getInstructionsSuggestions(
        context: ClientContext,
        params: GetAiSuggestionsParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<{ data: string[] }> {
        const client = this.getClient(context);
        if (!client.getInstructionsSuggestions) {
            throw new Error(`getInstructionsSuggestions not supported by ${client.constructor.name}`);
        }
        return client.getInstructionsSuggestions(params, config);
    }

    testAgent(context: ClientContext, params: TestAiVettingAgentParams): AxiosPromise<TestAiVettingAgentResponse> {
        const client = this.getClient(context);
        if (!client.testAgent) {
            throw new Error(`testAgent not supported by ${client.constructor.name}`);
        }
        return client.testAgent(params);
    }
}

export default new AiMessageVettingAgentsClient();
